import { fromEntries } from '@utils/fromEntries';
import { sentry } from './sentry';
import {
  TZDB_AFFECTED_ZONES,
  TZDB_AFFECTED_ZONE_NAMES,
  TzdbAffectedZone,
  TzdbProbeResult,
  TzdbZoneExpectation,
  getTzdbProbeResult,
  getTzdbVerdictMap,
  getTzdbZoneAttributeSuffix,
} from './time/tzdbRuleset';
import { win } from './win';

const CANONICAL_ZONES = TZDB_AFFECTED_ZONE_NAMES.filter(
  (zone: TzdbAffectedZone) =>
    !(TZDB_AFFECTED_ZONES[zone] as TzdbZoneExpectation).isAlias,
);

/** Sentry tag keys are limited to 32 chars, e.g. `tzdb.rainyriver` */
export const getTzdbSentryTags = (
  result: TzdbProbeResult,
): Record<string, string> => {
  const tags: Record<string, string> = { 'tzdb.summary': result.summary };
  for (const zone of CANONICAL_ZONES) {
    const key = `tzdb.${getTzdbZoneAttributeSuffix(zone).toLowerCase()}`;
    tags[key] = result.zones[zone].verdict;
  }
  return tags;
};

export const getTzdbSentryContext = (
  result: TzdbProbeResult,
): Record<string, unknown> => ({
  summary: result.summary,
  verdicts: getTzdbVerdictMap(result),
  observedOffsets: fromEntries(
    TZDB_AFFECTED_ZONE_NAMES.map((zone): [TzdbAffectedZone, string | null] => [
      zone,
      result.zones[zone].observedOffset,
    ]),
  ),
});

/** e.g. `{ tzdbSummary: '2026c-equivalent', tzWinnipegRuleset: 'old' }` */
export const getTzdbNewRelicAttributes = (
  result: TzdbProbeResult,
): Record<string, string> => {
  const attrs: Record<string, string> = { tzdbSummary: result.summary };
  for (const zone of TZDB_AFFECTED_ZONE_NAMES) {
    attrs[`tz${getTzdbZoneAttributeSuffix(zone)}Ruleset`] =
      result.zones[zone].verdict;
  }
  return attrs;
};

/**
 * FullStory typed var names, e.g. `tzdbSummary_str`, `tzWinnipegRuleset_str`.
 * Canonical zones only: user property names count toward FullStory's
 * org-wide cap of 500.
 */
export const getTzdbFullstoryVars = (
  result: TzdbProbeResult,
): Record<string, string> => {
  const vars: Record<string, string> = { tzdbSummary_str: result.summary };
  for (const zone of CANONICAL_ZONES) {
    vars[`tz${getTzdbZoneAttributeSuffix(zone)}Ruleset_str`] =
      result.zones[zone].verdict;
  }
  return vars;
};

/** Attaches the browser tzdb ruleset to Sentry and New Relic. Never throws. */
export const initTzdbObservability = (): void => {
  try {
    const result = getTzdbProbeResult();
    sentry.setTags(getTzdbSentryTags(result));
    sentry.setContext('tzdb', getTzdbSentryContext(result));
    if (win.newrelic?.setCustomAttribute) {
      for (const [key, value] of Object.entries(
        getTzdbNewRelicAttributes(result),
      )) {
        win.newrelic.setCustomAttribute(key, value, true);
      }
    }
  } catch {
    // Observability must never break app boot
  }
};

let fullstoryVarsSent = false;

/**
 * Attaches the browser tzdb ruleset to the FullStory user once per page load,
 * since callers run on every render and FullStory rate limits user property
 * calls to 30/min per session. Never throws.
 */
export const setTzdbFullstoryVars = (): void => {
  if (fullstoryVarsSent || !win.FS) {
    return;
  }
  try {
    win.FS.setUserVars(getTzdbFullstoryVars(getTzdbProbeResult()));
    fullstoryVarsSent = true;
  } catch {
    // noop
  }
};

/** @internal test-only */
export const resetTzdbFullstoryVarsSent = (): void => {
  fullstoryVarsSent = false;
};
